import { useId, type HTMLAttributes, type ReactNode } from 'react'
import { IconTile } from '../controls/IconTile'
import { cx } from '../controls/cx'
import s from './CardRecords.module.css'

export interface CardRecord {
  /** Stable identity — a record id, never the title. */
  key: string
  title: ReactNode
  /** The muted line under the title: "Commercial · 2h ago". */
  meta?: ReactNode
  /** The right-aligned value: an amount, a role, an environment. */
  value?: ReactNode
  /** The row's mark, in a small tile. Decorative. */
  icon?: ReactNode
  /**
   * Opens the record through the HOST's navigation. Present ⇒ the title is a
   * real button and the whole plate is its target; absent ⇒ the row is text.
   * An offer is a prop passed, never a no-op handler.
   */
  onOpen?: () => void
}

export interface CardRecordsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  records: readonly CardRecord[]
  /** Names the list for a screen reader, when the card's heading does not. */
  label?: string
  /**
   * WRAP the title and meta instead of ellipsising them (#2444 phase 4).
   *
   * The default one-line plate is right for a dashboard card, where a row is a
   * pointer to somewhere else. It is wrong where the row's words ARE the
   * content — an audit event, a posture check — because the ellipsis cuts the
   * very clause a reader needs ("… via MCP · Claude conn…") with no way to
   * reach the rest, and anything inline after it (a trust badge) is clipped
   * out of sight at phone width. Measured on `/ops/audit` at 390px before this
   * existed: every trust badge sat outside its row, invisible.
   */
  wrap?: boolean
}

/**
 * THE RECORDS WELL (#2406, "Dashboards and Cards", the records variant):
 * card slab → RECESS well → PLATE rows. Each row: a mark tile, the title over
 * its meta line, and a right-aligned value.
 *
 * One control per row, named by its title: the title is the button, and its
 * target is stretched over the plate so the whole row is hit — the pattern a
 * card of records wants, without a button nested in a button and without
 * making the value or the meta line part of the name a reader hears. The meta
 * is the button's DESCRIPTION instead, so it is still read, after the name.
 *
 * An empty list renders NOTHING. "No records" is the card's own `empty` state
 * (`DashboardCard state="empty"`), which says what will appear — an empty well
 * is a container with nothing to say.
 */
export function CardRecords({ records, label, wrap = false, className, ...rest }: CardRecordsProps) {
  const base = useId()
  if (records.length === 0) return null
  return (
    <div className={cx(s.well, className)} data-card-records="" {...rest}>
      <ul className={s.plate} aria-label={label} data-wrap={wrap ? '' : undefined}>
        {records.map((record, index) => {
          const metaId = `${base}-${index}-meta`
          const hasMeta = record.meta != null && record.meta !== false && record.meta !== ''
          return (
            <li key={record.key} className={s.row} data-openable={record.onOpen ? '' : undefined}>
              {record.icon != null && (
                <IconTile size="sm" className={s.mark} aria-hidden="true">{record.icon}</IconTile>
              )}
              <span className={s.text}>
                {record.onOpen
                  ? (
                    <button type="button" className={cx(s.title, s.open)} onClick={record.onOpen}
                      aria-describedby={hasMeta ? metaId : undefined}>
                      {record.title}
                    </button>
                  )
                  : <span className={s.title}>{record.title}</span>}
                {hasMeta && <span className={s.meta} id={metaId}>{record.meta}</span>}
              </span>
              {record.value != null && record.value !== false && (
                <span className={cx(s.value, 'tnum')}>{record.value}</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
